<template>
  <div class="km-chats">
    <!-- ===== TOP BAR ===== -->
    <header class="kc-top">
      <div class="kc-top__inner">
        <div class="kc-top__left">
          <h1>Messages</h1>
          <span class="kc-top__count" v-if="unreadCount">{{ unreadCount }}</span>
        </div>
        <button class="kc-top__icon" @click="toggleSearch" aria-label="Search">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
        </button>
      </div>
      <transition name="kc-search">
        <div v-if="showSearch" class="kc-search">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input v-model="query" placeholder="Search conversations…" autofocus/>
          <button v-if="query" class="kc-search__clear" @click="query=''">✕</button>
        </div>
      </transition>
    </header>

    <main class="kc-main">
      <!-- ===== NEW MATCHES STRIP ===== -->
      <section class="kc-section" v-if="newMatches.length">
        <div class="kc-sec-head">
          <h3>✨ New matches</h3>
          <span class="kc-sec-hint">Say hi first!</span>
        </div>
        <div class="kc-strip">
          <button class="kc-strip__item kc-strip__item--add" @click="$router.push('/Discover')">
            <div class="kc-strip__img kc-strip__add">
              <span>＋</span>
            </div>
            <span class="kc-strip__name">Find more</span>
          </button>
          <button
            v-for="m in newMatches" :key="m.id"
            class="kc-strip__item"
            @click="openChat(m)"
          >
            <div class="kc-strip__img" :style="{backgroundImage:'url('+m.avatar+')'}">
              <span v-if="m.online" class="kc-strip__dot"></span>
            </div>
            <span class="kc-strip__name">{{ m.name }}</span>
          </button>
        </div>
      </section>

      <!-- ===== CONVERSATION LIST ===== -->
      <section class="kc-section">
        <div class="kc-sec-head">
          <h3>💬 Chats</h3>
          <button class="kc-filter" v-if="filter !== 'all'" @click="filter='all'">Clear filter</button>
        </div>

        <div class="kc-filters">
          <button class="kc-chip" :class="{active: filter==='all'}" @click="filter='all'">All</button>
          <button class="kc-chip" :class="{active: filter==='unread'}" @click="filter='unread'">Unread {{ unreadCount ? '· '+unreadCount : '' }}</button>
          <button class="kc-chip" :class="{active: filter==='pinned'}" @click="filter='pinned'">📌 Pinned</button>
        </div>

        <div v-if="filteredChats.length === 0" class="kc-empty">
          <div class="kc-empty__emoji">💌</div>
          <h3>No conversations yet</h3>
          <p>Match with someone on Discover, then come back here to start chatting.</p>
          <button class="kc-empty__btn" @click="$router.push('/Discover')">Start swiping</button>
        </div>

        <div v-else class="kc-card">
          <button
            v-for="c in filteredChats" :key="c.id"
            class="kc-row" :class="{ unread: c.unread > 0 }"
            @click="openChat(c)"
          >
            <div class="kc-row__avatar">
              <img :src="c.avatar" :alt="c.name" @error="c.avatar = fallbackAvatar(c.id)"/>
              <span v-if="c.online" class="kc-row__dot"></span>
              <span v-if="c.verified" class="kc-row__verified">✓</span>
            </div>
            <div class="kc-row__body">
              <div class="kc-row__head">
                <span class="kc-row__name">
                  {{ c.name }}
                  <span v-if="c.pinned" class="kc-row__pin">📌</span>
                </span>
                <span class="kc-row__time">{{ c.time }}</span>
              </div>
              <div class="kc-row__msg">
                <span class="kc-row__preview">
                  <span v-if="c.you" class="kc-row__you">You: </span>{{ c.preview }}
                </span>
                <span v-if="c.unread" class="kc-row__badge">{{ c.unread }}</span>
              </div>
            </div>
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
const NAMES = ['Sophia','Emma','Aiko','Maya','Noelle','Priya','Lila','Zoe','Chloe','Ava','Ruby','Luna','Mia','Isla','Nina','Yuki'];
const IMGS = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1534751516642-a1af1ef26a56?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1517365830460-955ce3ccd263?w=200&h=200&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=200&h=200&fit=crop&crop=faces'
];
const PREVIEWS = [
  'Haha that story is so good 😂',
  'Are we still on for Friday?',
  'Just finished my coffee ☕',
  'The photos look amazing!',
  'I was thinking about the place you mentioned',
  'Sent you a song, you\'ll love it 🎵',
  'That movie was wild 🎬',
  'Have a great day 💖',
  'OMG yes let\'s do it!',
  'Good morning ☀️',
  'Thinking of you',
  'You\'re hilarious'
];
const TIMES = ['now','2 min','15 min','1 h','3 h','Today','Yesterday','Mon','Tue','Wed'];

function build(i){
  const seed = i + 7;
  return {
    id: i,
    name: NAMES[seed % NAMES.length],
    avatar: IMGS[seed % IMGS.length],
    preview: PREVIEWS[seed % PREVIEWS.length],
    time: TIMES[seed % TIMES.length],
    unread: (seed % 5 === 0) ? ((seed % 4) + 1) : 0,
    online: (seed % 3) !== 0,
    verified: (seed % 4) !== 0,
    pinned: seed % 9 === 0,
    you: seed % 3 === 1
  };
}

export default {
  name: 'KmChats',
  data(){
    return {
      query: '',
      filter: 'all',
      showSearch: false,
      conversations: Array.from({ length: 9 }, (_, i) => build(i + 1)),
      newMatches: Array.from({ length: 6 }, (_, i) => ({
        id: 'm' + i,
        name: NAMES[(i*3 + 5) % NAMES.length],
        avatar: IMGS[(i*3 + 2) % IMGS.length],
        online: i % 2 === 0
      }))
    };
  },
  computed: {
    unreadCount(){ return this.conversations.reduce((s, c) => s + (c.unread || 0), 0); },
    filteredChats(){
      let list = this.conversations.slice();
      if(this.query){
        const q = this.query.toLowerCase();
        list = list.filter(c => c.name.toLowerCase().includes(q) || c.preview.toLowerCase().includes(q));
      }
      if(this.filter === 'unread')  list = list.filter(c => c.unread > 0);
      if(this.filter === 'pinned')  list = list.filter(c => c.pinned);
      // Sort: pinned first, then by unread, then as-is
      return list.sort((a, b) => {
        if(a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        if((b.unread||0) !== (a.unread||0)) return (b.unread||0) - (a.unread||0);
        return 0;
      });
    }
  },
  methods: {
    toggleSearch(){ this.showSearch = !this.showSearch; if(!this.showSearch) this.query = ''; },
    openChat(c){
      this.$router.push({ path: '/Chat', query: { id: c && c.id != null ? String(c.id) : '1' } });
    },
    fallbackAvatar(id){
      const hash = Math.abs((id + '').split('').reduce((a,c) => a + c.charCodeAt(0), 0));
      return IMGS[hash % IMGS.length];
    }
  },
  created(){
    if(!localStorage.getItem('token')) this.$router.replace('/Login');
  }
};
</script>

<style scoped>
.km-chats {
  min-height: 100vh;
  background:
    radial-gradient(800px 400px at -10% 0%, rgba(255,77,141,.08), transparent 60%),
    radial-gradient(700px 400px at 110% 10%, rgba(160,32,240,.08), transparent 60%),
    linear-gradient(180deg, #fdf5f9 0%, #f6eef3 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #2a1a3f;
  padding-bottom: 140px;
}
.km-chats * { box-sizing: border-box; }
button { font-family: inherit; }

/* Top bar */
.kc-top {
  position: sticky; top: 0; z-index: 30;
  background: rgba(255,255,255,.88);
  backdrop-filter: blur(22px) saturate(170%);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  border-bottom: 1px solid rgba(255,77,141,.1);
}
.kc-top__inner {
  max-width: 720px; margin: 0 auto;
  padding: 14px 18px;
  display: flex; align-items: center; justify-content: space-between;
}
.kc-top__left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.kc-top__inner h1 {
  margin: 0; font-size: 22px; font-weight: 800; letter-spacing: -.02em;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.kc-top__count {
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 12px; font-weight: 800;
  padding: 3px 10px; border-radius: 999px;
  box-shadow: 0 4px 10px rgba(255,77,141,.3);
}
.kc-top__icon {
  width: 40px; height: 40px; border-radius: 12px;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  cursor: pointer; color: #2a1a3f;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, transform .15s;
}
.kc-top__icon:hover { border-color: #ff4d8d; transform: translateY(-1px); }

/* Search */
.kc-search {
  max-width: 720px; margin: 0 auto;
  display: flex; align-items: center; gap: 10px;
  padding: 0 18px 12px;
  color: #8273a0;
}
.kc-search::before { content: ''; }
.kc-search input {
  flex: 1; border: 1.5px solid rgba(255,77,141,.18); outline: none;
  padding: 10px 14px; border-radius: 14px;
  background: #fff; font-family: inherit; font-size: 14px; color: #2a1a3f;
  font-weight: 500;
  transition: border-color .2s, box-shadow .2s;
}
.kc-search input:focus { border-color: #ff4d8d; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }
.kc-search input::placeholder { color: #b8a9c9; }
.kc-search__clear {
  background: none; border: none; cursor: pointer;
  color: #b8a9c9; font-size: 14px; padding: 4px 6px;
}
.kc-search-enter-active, .kc-search-leave-active { transition: opacity .2s ease, max-height .25s ease; overflow: hidden; }
.kc-search-enter, .kc-search-leave-to { opacity: 0; max-height: 0; }
.kc-search-enter-to, .kc-search-leave { opacity: 1; max-height: 60px; }

/* Main */
.kc-main { max-width: 720px; margin: 0 auto; }
.kc-section { padding: 16px 18px 0; }
.kc-section + .kc-section { padding-top: 8px; }
.kc-sec-head {
  display: flex; align-items: baseline; justify-content: space-between;
  margin-bottom: 12px;
}
.kc-sec-head h3 {
  margin: 0; font-size: 15px; font-weight: 800; letter-spacing: -.01em; color: #2a1a3f;
}
.kc-sec-hint { font-size: 12px; color: #8d7fa6; }
.kc-filter {
  background: none; border: none; cursor: pointer;
  color: #ff4d8d; font-size: 12px; font-weight: 700;
}

/* New matches strip */
.kc-strip {
  display: flex; gap: 14px; overflow-x: auto; scrollbar-width: none;
  padding-bottom: 4px; margin: 0 -18px; padding-left: 18px; padding-right: 18px;
}
.kc-strip::-webkit-scrollbar { display: none; }
.kc-strip__item {
  flex-shrink: 0; width: 72px; text-align: center;
  background: none; border: none; cursor: pointer; padding: 0;
  font-family: inherit;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
}
.kc-strip__img {
  position: relative; width: 64px; height: 64px;
  border-radius: 50%; background-size: cover; background-position: center;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2.5px rgba(255,77,141,.6), 0 6px 14px rgba(160,32,240,.2);
  transition: transform .2s;
}
.kc-strip__item:hover .kc-strip__img { transform: translateY(-2px); }
.kc-strip__dot {
  position: absolute; bottom: 2px; right: 2px;
  width: 14px; height: 14px; border-radius: 50%; background: #10b981;
  border: 3px solid #fff;
}
.kc-strip__add {
  background: linear-gradient(135deg, #fff0f6, #ede1ff) !important;
  color: #a020f0;
  font-size: 28px; font-weight: 300;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 0 2.5px rgba(160,32,240,.3), 0 6px 14px rgba(160,32,240,.15);
}
.kc-strip__name {
  font-size: 12px; font-weight: 600; color: #4a3d66;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 72px;
}

/* Filter chips */
.kc-filters { display: flex; gap: 8px; margin-bottom: 12px; overflow-x: auto; scrollbar-width: none; }
.kc-filters::-webkit-scrollbar { display: none; }
.kc-chip {
  flex-shrink: 0; padding: 7px 14px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(26,16,51,.08);
  background: #fff; color: #4a3d66; font-family: inherit;
  font-size: 13px; font-weight: 600;
  transition: all .2s;
}
.kc-chip:hover { border-color: #ff4d8d; }
.kc-chip.active {
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  color: #fff; border-color: transparent;
  box-shadow: 0 6px 14px rgba(255,77,141,.25);
}

/* Conversation card list */
.kc-card {
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 18px;
  border: 1px solid rgba(255,77,141,.12);
  box-shadow: 0 6px 18px rgba(255,77,141,.08), inset 0 1px 0 rgba(255,255,255,.9);
  overflow: hidden;
}
.kc-row {
  display: flex; gap: 12px; align-items: center;
  width: 100%; padding: 14px 16px;
  background: transparent; border: none; cursor: pointer;
  border-bottom: 1px solid rgba(255,77,141,.08);
  text-align: left; font-family: inherit;
  transition: background .15s;
}
.kc-row:last-child { border-bottom: none; }
.kc-row:hover { background: rgba(255,77,141,.04); }
.kc-row.unread { background: rgba(255,77,141,.03); }
.kc-row__avatar {
  position: relative; flex-shrink: 0;
  width: 52px; height: 52px;
}
.kc-row__avatar img {
  width: 100%; height: 100%; border-radius: 50%; object-fit: cover;
  background: #faf3f8;
}
.kc-row__dot {
  position: absolute; bottom: 1px; right: 1px;
  width: 14px; height: 14px; border-radius: 50%; background: #10b981;
  border: 3px solid #fff6fa;
}
.kc-row__verified {
  position: absolute; top: -2px; right: -2px;
  width: 18px; height: 18px; border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff; font-size: 10px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #fff;
}
.kc-row__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.kc-row__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.kc-row__name {
  font-size: 15px; font-weight: 700; color: #2a1a3f;
  display: flex; align-items: center; gap: 5px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kc-row__pin { font-size: 11px; }
.kc-row__time {
  font-size: 11px; color: #8273a0; font-weight: 500;
  flex-shrink: 0;
}
.kc-row.unread .kc-row__time { color: #ff4d8d; font-weight: 700; }
.kc-row__msg {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.kc-row__preview {
  font-size: 13px; color: #6b5c85; line-height: 1.3;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  flex: 1; min-width: 0;
}
.kc-row.unread .kc-row__preview { color: #2a1a3f; font-weight: 600; }
.kc-row__you { color: #8d7fa6; }
.kc-row__badge {
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 11px; font-weight: 800;
  padding: 2px 8px; border-radius: 999px; min-width: 22px; text-align: center;
  box-shadow: 0 4px 10px rgba(255,77,141,.3);
  flex-shrink: 0;
}

/* Empty state */
.kc-empty {
  text-align: center; padding: 50px 20px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1px dashed rgba(255,77,141,.25);
  border-radius: 20px;
}
.kc-empty__emoji { font-size: 54px; margin-bottom: 12px; }
.kc-empty h3 { margin: 0 0 6px; font-size: 17px; color: #2a1a3f; }
.kc-empty p { margin: 0 0 20px; font-size: 13px; color: #8273a0; line-height: 1.5; }
.kc-empty__btn {
  padding: 12px 26px; border-radius: 999px; border: none; cursor: pointer;
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  font-weight: 700; font-size: 14px;
  box-shadow: 0 10px 24px rgba(255,77,141,.3);
  transition: transform .15s;
}
.kc-empty__btn:hover { transform: translateY(-2px); }

/* Desktop */
@media (min-width: 720px) {
  .kc-top__inner { padding: 18px 24px; }
  .kc-top__inner h1 { font-size: 26px; }
  .kc-main { padding: 6px 0 40px; }
  .kc-section { padding: 20px 24px 0; }
  .kc-strip__img { width: 74px; height: 74px; }
  .kc-strip__item { width: 84px; }
  .kc-row { padding: 16px 20px; }
  .kc-row__avatar { width: 58px; height: 58px; }
  .kc-row__name { font-size: 16px; }
  .kc-row__preview { font-size: 14px; }
}
</style>
